import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/client/react';
import dayjs from 'dayjs';
import { Button, Flex, Typography } from 'antd';

import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import useModalControls from 'hooks/useModalControls';
import { PAYMENTS } from 'queries/payments';
import { formatDateToSubmit } from 'utilities/datesAndTimes';

import {
  deduplicateScheduledPayments,
  updatePaymentsWithPaymentState,
} from '../paymentUtils';
import PrecoEditPaymentModal from './PrecoEditPaymentModal';
import PrecoScheduleTable from './PrecoScheduleTable';
import PrecoUpdatePaymentPlanModal from './PrecoUpdatePaymentPlanModal';
import {
  getPrecoBalanceAlert,
  getPrecoScheduleTotal,
  getScheduledTransactions,
} from './precoPaymentUtils';

const { Text, Title } = Typography;

const PrecoPaymentPlan = ({
  offerId,
  payments,
  paymentPlan,
  createdAt,
  expirationDate,
  settlementBalance,
  originalOutstandingBalance,
  titleAction,
}) => {
  const { accountId, customerId } = useParams();
  const editPaymentModal = useModalControls();
  const updatePaymentPlanModal = useModalControls();

  const [localSchedule, setLocalSchedule] = useState(
    getScheduledTransactions(paymentPlan),
  );
  const [initialValues, setInitialValues] = useState({});
  const [updatePaymentType, setUpdatePaymentType] = useState('add');

  const hasPendingScheduleChanges = localSchedule.some(
    (payment) => payment.changeType,
  );

  useEffect(() => {
    setLocalSchedule(getScheduledTransactions(paymentPlan));
  }, [paymentPlan?.transactions]);

  const updatedTransactions = useMemo(
    () =>
      localSchedule
        .filter((payment) => payment.changeType !== 'delete')
        .map(({ amount, date }) => ({ amount, date })),
    [localSchedule],
  );

  const paymentsQuery = useQuery(PAYMENTS, {
    variables: {
      accountId,
      customerId,
      fromDate: formatDateToSubmit(dayjs(createdAt).subtract(1, 'day')),
      toDate: formatDateToSubmit(dayjs().add(1, 'day')),
    },
    fetchPolicy: 'network-only',
  });

  const correctedPastPayments = useMemo(
    () =>
      updatePaymentsWithPaymentState(
        payments,
        paymentsQuery.data?.account.payments ?? [],
      ),
    [payments, paymentsQuery.data],
  );

  const tableData = useMemo(() => {
    const dedupedScheduled = deduplicateScheduledPayments(
      correctedPastPayments,
      localSchedule,
    );

    return [
      ...(correctedPastPayments ?? []).map((payment) => ({
        ...payment,
        editable: false,
      })),
      ...dedupedScheduled.map((payment) => ({ ...payment, editable: true })),
    ]
      .map((payment, index) => ({ ...payment, key: `payment-${index}` }))
      .sort((a, b) => dayjs(a.date) - dayjs(b.date));
  }, [correctedPastPayments, localSchedule]);

  const totalPayments = useMemo(
    () => getPrecoScheduleTotal(tableData),
    [tableData],
  );

  const balanceAlert = useMemo(
    () =>
      getPrecoBalanceAlert({
        totalPayments,
        balance: settlementBalance,
        outstandingBalance: originalOutstandingBalance,
      }),
    [totalPayments, settlementBalance, originalOutstandingBalance],
  );

  const onDiscardChanges = () => {
    setLocalSchedule(getScheduledTransactions(paymentPlan));
  };

  const onAddPayment = () => {
    setInitialValues({});
    setUpdatePaymentType('add');
    editPaymentModal.show();
  };

  const onEditPayment = (record) => {
    setInitialValues({
      amount: Number((record.amount / 100).toFixed(2)),
      date: dayjs(record.date),
    });
    setUpdatePaymentType('edit');
    editPaymentModal.show();
  };

  return (
    <>
      <Flex align="center" justify="space-between">
        <Title level={5} style={{ margin: 0 }}>
          Payments
        </Title>
        {titleAction}
      </Flex>
      {tableData.length ? (
        <QueryBoundary
          query={paymentsQuery}
          mode={QueryBoundary.MODE.MESSAGE}
          loadingMessage="Loading Payments"
        >
          <PrecoScheduleTable
            data={tableData}
            schedule={localSchedule}
            setSchedule={setLocalSchedule}
            onEditPayment={onEditPayment}
            balanceAlert={balanceAlert}
          />
        </QueryBoundary>
      ) : (
        <Text type="secondary">No payments scheduled.</Text>
      )}
      <Flex justify="flex-end" gap="var(--spacing-sm)">
        {hasPendingScheduleChanges && (
          <Button type="text" onClick={onDiscardChanges}>
            Discard Changes
          </Button>
        )}
        <Button onClick={onAddPayment}>Add a Payment</Button>
        <Button
          type="primary"
          disabled={
            !hasPendingScheduleChanges || balanceAlert?.type === 'error'
          }
          onClick={updatePaymentPlanModal.show}
        >
          Update Payment Plan
        </Button>
      </Flex>
      <PrecoEditPaymentModal
        {...editPaymentModal}
        transactions={tableData.filter(
          (payment) => payment.changeType !== 'delete',
        )}
        schedule={localSchedule}
        setSchedule={setLocalSchedule}
        expirationDate={expirationDate}
        initialValues={initialValues}
        setInitialValues={setInitialValues}
        type={updatePaymentType}
      />
      <PrecoUpdatePaymentPlanModal
        {...updatePaymentPlanModal}
        offerId={offerId}
        transactions={updatedTransactions}
        fundingAccount={paymentPlan?.fundingAccount}
      />
    </>
  );
};

export default PrecoPaymentPlan;
